import {
  Component,
  ChangeDetectionStrategy,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  Inject,
  ChangeDetectorRef
} from '@angular/core';
import { FilterCriteria } from '../../filter/filter-criteria';
import { FilterData } from '../../filter/filter-data';
import { VirtualGridColumn } from '../../types/virtual-grid-column';
import { VirtualGridHeaderDropdownComponent } from '../virtual-grid-header-dropdown/virtual-grid-header-dropdown.component';

import * as Utils from '../../utils';
import { ChangeDetector } from '@wfm/core';
import { DOCUMENT } from '@angular/common';

export interface ColumnState {
  column: VirtualGridColumn;
  filterData?: FilterData;
  filterDescription?: string;
  index: number;
  isFilterActive: boolean;
}

@Component({
  selector: 'virtual-grid-card-filter',
  templateUrl: './virtual-grid-card-filter.component.html',
  styleUrls: ['./virtual-grid-card-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VirtualGridCardFilterComponent extends ChangeDetector implements OnChanges, OnDestroy {
  private activeColumnState: ColumnState;

  columnStateList: ColumnState[] = [];

  @Input()
  isActive: boolean;

  @Input()
  layout: VirtualGridColumn[];

  @Input()
  include: string[];

  @Input()
  filterCriteria: FilterCriteria[];

  @Output()
  closeRequest?: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  filterCriteriaChange: EventEmitter<FilterCriteria[]> = new EventEmitter<FilterCriteria[]>();

  @ViewChildren(VirtualGridHeaderDropdownComponent)
  dropdowns: QueryList<VirtualGridHeaderDropdownComponent>;

  constructor(@Inject(DOCUMENT) document: any, cd: ChangeDetectorRef) {
    super((document as Document).defaultView, cd);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.layout || changes.include) {
      const orderMap: Record<string, number> = {};

      // Build a map of the field to its index.
      (this.include || []).forEach((columnId, index) => {
        orderMap[columnId] = index;
      });

      this.columnStateList = (this.layout || [])
        // Filter the column layout by filterable columns that are included in the given list.
        .filter(column => {
          return column.filterConfig && orderMap[column.id] !== undefined;
        })
        // Sort the columns by the order provided in the 'include' list.
        .sort((columnA, columnB) => {
          // We know both columns exist in the order map because they would have been filtered
          // out otherwise.
          return orderMap[columnA.id] - orderMap[columnB.id];
        })
        // Then map these columns to a ColumnState object.
        .map((column, index) => {
          return this.updateFilterData(
            {
              column: column,
              index: index,
              isFilterActive: false
            },
            this.getFilterData(column.id)
          );
        });
    }

    if (changes.isActive && !this.isActive) {
      this.closeActiveFilter();
    }

    if (changes.filterCriteria || changes.isActive) {
      this.columnStateList?.forEach(columnState => {
        this.updateFilterData(columnState, this.getFilterData(columnState.column.id));
      });
    }
  }

  ngOnDestroy(): void {
    // WFM recognizes the existance of this method to completely destroy this component.
  }

  onAccordionClick(columnState: ColumnState): void {
    // Regardless of whether we're toggling an accordion item or activating a new one, if there is an
    // active column state we want to first deactivate it and attempt to apply the filter.
    this.applyActiveFilter();

    // Save this off, as closeActiveFilter will reset it.
    const isFilterActive = columnState.isFilterActive;

    // Also close the active filter.
    this.closeActiveFilter();

    // If the accordion item clicked is not currently active, then activate it.
    if (!isFilterActive) {
      this.activateFilter(columnState);
    }

    this.$detectChanges();
  }

  onApply(): void {
    // Apply the active filter (if any), but don't close it.  Keep it open as the user has it.
    this.applyActiveFilter();

    // Copy the array.
    const filterCriteria = [...(this.filterCriteria || [])];

    // Iterate through all the columns.
    this.columnStateList.forEach(columnState => {
      // Find the index (if any) of the existing filter.
      const idx = filterCriteria.findIndex(value => value.column.id === columnState.column.id);

      if (columnState.filterData) {
        if (idx === -1) {
          // Add to the end.
          filterCriteria.push({
            column: columnState.column,
            filterData: columnState.filterData
          });
        } else {
          // Update the filter data at the index.
          filterCriteria[idx].filterData = columnState.filterData;
        }
      } else {
        // removed
        if (idx === -1) {
          // No op.
        } else {
          filterCriteria.splice(idx, 1);
        }
      }
    });

    this.filterCriteriaChange.emit(filterCriteria);
    this.closeRequest.emit();
  }

  onClear(): void {
    this.filterCriteriaChange.emit([]);
    this.closeRequest.emit();
  }

  onCloseRequest(columnState: ColumnState, next?: boolean): void {
    // Sanity check.
    if (this.activeColumnState) {
      Utils.assert(this.activeColumnState.column.id === columnState.column.id, 'Error in active column state.');
    }

    // No need to generally handle the close request, as this is handled in response to a user click on an
    // accordion item ( see onAccordionClick ).

    // This handles a Tab or Shift+Tab to cycle through the dropdowns.
    if (next !== undefined) {
      // However in the case the user hit the Tab or Shift+Tab key, the dropdown will internally apply the
      // filter and request to close the current active filter.  So we need to handle that case.
      this.closeActiveFilter();

      // Get the index of the column state.
      let index = columnState.index;

      if (next) {
        if (++index < this.columnStateList.length) {
          this.window.setTimeout(() => {
            this.activateFilter(this.columnStateList[index]);
          });
        }
      } else {
        if (--index >= 0) {
          this.window.setTimeout(() => {
            this.activateFilter(this.columnStateList[index]);
          });
        }
      }
    }
  }

  onFilterDataChange(columnState: ColumnState, filterData: FilterData): void {
    this.updateFilterData(columnState, filterData);
  }

  private activateFilter(columnState: ColumnState): void {
    // The assumption due to the business logic of this class is that this method is only called when there is no
    // active column state.
    Utils.assert(this.activeColumnState === undefined, 'There should be no active column state here');

    this.activeColumnState = columnState;
    this.activeColumnState.isFilterActive = true;
    this.$detectChanges();
  }

  private applyActiveFilter(): void {
    if (this.activeColumnState) {
      // The 'onApply' call in the dropdown will issue a 'filterDataChange' if there is a change, and
      // always a 'closeRequest' event.
      this.dropdowns.toArray()[this.activeColumnState.index].onApply();
    }
  }

  private closeActiveFilter(): void {
    if (this.activeColumnState) {
      this.activeColumnState.isFilterActive = false;
      this.activeColumnState = undefined;
    }
  }

  private getFilterData(columnId: string): FilterData {
    return this.filterCriteria?.find(filterCriteria => filterCriteria.column.id === columnId)?.filterData;
  }

  private getFilterDescription(filterData: FilterData, filterParams: any): string {
    const filterTags = filterData?.getTags(filterParams);
    return filterTags?.map(filterTag => filterTag.description).join(', ');
  }

  private updateFilterData(columnState: ColumnState, filterData: FilterData): ColumnState {
    // Update the filter data in the column state.
    columnState.filterData = filterData;
    // Update the filter description.
    columnState.filterDescription = this.getFilterDescription(filterData, columnState.column.filterConfig.filterParams);
    // Return the new column state.
    return columnState;
  }
}
